import React, { useCallback, useEffect, useMemo } from "react";
import { endOfYear, formatISO, parseISO, startOfYear } from "date-fns";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { getAccountingBasisLabel } from "@common/helpers/accountingBasis";
import { useHasAnyAccrualLedger, useLedgersByEntityIds } from "@api/ledger.api";
import { useBusinessNonMockEntityIds } from "@hooks/useBusinessEntityIds";
import { EntityFilterCombobox } from "@components/entity/EntityFilter/EntityFilterCombobox";
import { EntityFilterType } from "@components/entity/EntityFilter/types";
import {
    LedgerBasisOption,
    LedgerBasisSelect,
} from "@components/ledger/LedgerBasisSelect";
import { YearSelect } from "@components/settings/YearFilter/YearSelect";
import { getMultiEntityBasisInfo } from "@components/accounting/generalLedger/multiEntityLedger";
import { DateRangeFilterNext } from "@design-system/DateRangeFilter/DateRangeFilterNext";
import type { DateOnlyDateRange } from "@design-system/DateRangeFilter/DateRangeFilterNext.lib";
import { DirectoryFilters } from "./lib";
import { useAvailableYears } from "./useAvailableYears";

const ACCRUAL_UNAVAILABLE_TOOLTIP =
    "Some selected entities have no accrual ledger.";

interface Props {
    filters: DirectoryFilters;
    setFilters: (filters: DirectoryFilters) => void;
    /**
     * Restricts the entity filter to a single entity (1099 review views).
     * Multi-selections are trimmed to the most recently added entity and the
     * selection cannot be cleared back to "all entities".
     */
    singleEntitySelection?: boolean;
    /**
     * Locks the entity and date-range scope (applied 1099 tasks). The year and
     * entity controls become read-only so the task cannot be pointed at another
     * entity or calendar year than the one it was assigned at creation.
     */
    lockScope?: boolean;
    hideEntityFilter?: boolean;
    hideBasisFilter?: boolean;
}

export const DirectoryFiltersForm: React.FC<Props> = ({
    filters,
    setFilters,
    singleEntitySelection,
    lockScope,
    hideEntityFilter,
    hideBasisFilter,
}) => {
    const businessEntityIds = useBusinessNonMockEntityIds();
    const availableYears = useAvailableYears();
    const hasAnyAccrualLedger = useHasAnyAccrualLedger(businessEntityIds);
    // Fetch ledgers for the stable business-entity superset (matches the GL
    // report) so narrowing the selection doesn't churn the ledger query.
    const { data: ledgersByEntityId } =
        useLedgersByEntityIds(businessEntityIds);

    const selectedEntityIds = useMemo(
        () =>
            filters.entityIds?.length ? filters.entityIds : businessEntityIds,
        [filters.entityIds, businessEntityIds],
    );

    const isAccrualAvailable = useMemo(
        () =>
            !!ledgersByEntityId &&
            getMultiEntityBasisInfo({
                ledgersByEntityId,
                selectedEntityIds,
            }).accrualLedgerId !== undefined,
        [ledgersByEntityId, selectedEntityIds],
    );

    const setDateRange = useCallback(
        ({ start, end }: DateOnlyDateRange, isAllTime?: boolean) => {
            setFilters({
                ...filters,
                startDate: start && !isAllTime ? start : undefined,
                endDate: end && !isAllTime ? end : undefined,
            });
        },
        [filters, setFilters],
    );

    const selectedYear = useMemo(() => {
        if (!filters.startDate || !filters.endDate) {
            return 0;
        }
        // startDate is a date-only ISO string, which parseISO interprets as
        // UTC midnight. Read the year back via getUTCFullYear (not the
        // local-time getYear) so the two stay consistent across timezones.
        return parseISO(filters.endDate).getUTCFullYear();
    }, [filters.startDate, filters.endDate]);

    const setYear = useCallback(
        (year: number) => {
            setFilters({
                ...filters,
                startDate:
                    year !== 0
                        ? formatISO(startOfYear(new Date(year, 0)), {
                              representation: "date",
                          })
                        : undefined,
                endDate:
                    year !== 0
                        ? formatISO(endOfYear(new Date(year, 0)), {
                              representation: "date",
                          })
                        : undefined,
            });
        },
        [filters, setFilters],
    );

    const setEntityIds = useCallback(
        (entityFilters: EntityFilterType) => {
            let entityIds = entityFilters.entityIds;
            if (singleEntitySelection) {
                if (!entityIds || entityIds.length === 0) {
                    // Keep the current entity — single-entity mode has no
                    // "all entities" state.
                    entityIds = filters.entityIds;
                } else if (entityIds.length > 1) {
                    const addedId = entityIds.find(
                        (id) => !filters.entityIds?.includes(id),
                    );
                    entityIds = [addedId ?? entityIds[0]];
                }
            }
            setFilters({ ...filters, entityIds });
        },
        [filters, setFilters, singleEntitySelection],
    );

    const setBasis = useCallback(
        (basis: string) => {
            const matchedBasis = Object.values(AccountingLedgerBasis).find(
                (value) => value === basis,
            );
            if (!matchedBasis) {
                return;
            }
            setFilters({
                ...filters,
                basis: matchedBasis,
            });
        },
        [filters, setFilters],
    );

    const basisOptions: LedgerBasisOption[] = useMemo(
        () => [
            {
                value: AccountingLedgerBasis.cash,
                label: getAccountingBasisLabel(AccountingLedgerBasis.cash),
            },
            {
                value: AccountingLedgerBasis.accruals,
                label: getAccountingBasisLabel(AccountingLedgerBasis.accruals),
                ...(isAccrualAvailable
                    ? {}
                    : {
                          disabled: true,
                          tooltip: ACCRUAL_UNAVAILABLE_TOOLTIP,
                      }),
            },
        ],
        [isAccrualAvailable],
    );

    // Revert a stale Accrual selection to Cash once accrual becomes unavailable
    // for the current entity selection, mirroring the GL report's cash fallback.
    useEffect(() => {
        if (
            filters.basis === AccountingLedgerBasis.accruals &&
            !isAccrualAvailable &&
            ledgersByEntityId
        ) {
            setFilters({ ...filters, basis: AccountingLedgerBasis.cash });
        }
    }, [filters, isAccrualAvailable, ledgersByEntityId, setFilters]);

    return (
        <>
            {singleEntitySelection ? (
                <YearSelect
                    value={selectedYear}
                    availableYears={availableYears}
                    onChange={setYear}
                    disabled={lockScope}
                />
            ) : (
                <DateRangeFilterNext
                    dateRange={{
                        start: filters.startDate,
                        end: filters.endDate,
                    }}
                    onChange={setDateRange}
                    disabled={lockScope}
                />
            )}
            {hasAnyAccrualLedger && !hideBasisFilter && (
                <LedgerBasisSelect
                    options={basisOptions}
                    value={filters.basis ?? AccountingLedgerBasis.cash}
                    onChange={setBasis}
                />
            )}
            {!hideEntityFilter && (
                <EntityFilterCombobox
                    filters={filters}
                    onChange={setEntityIds}
                    onlyBusiness
                    disabled={lockScope}
                />
            )}
        </>
    );
};
